import { useState } from 'react';
import styled from 'styled-components';
import { Header } from '../../goods-view/GoodsView';
import Typography from '../../../components/typography/Typography';
import Button from '../../../components/button/Button';
import TextField from '../../../components/text-field/TextField';
import Select, { MenuItem } from '../../../components/select/Select';
import NotificationService from '../../../services/NotificationService';
import { ApiException, WorkTaskStatus } from '../../../api/api';
import { getEnumDisplayValue } from '../../../utils/enumUtils';
import AdminService from '../../../services/AdminService';
import { log } from '../../../utils/logging/log';
import { linkToTask } from '../../task-view/task-details-view2/utils/LinkToWorkTask';

const AdminUpdateTaskStatusView = () => {
  const [taskId, setTaskId] = useState('');
  const [newStatus, setNewStatus] = useState<WorkTaskStatus | ''>('');
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = () => {
    setLoading(true);
    AdminService.adminUpdateTaskStatus({
      taskId: parseInt(taskId),
      newStatus: newStatus as WorkTaskStatus,
      reason: reason
    })
      .then(() => {
        NotificationService.showToast({
          severity: 'success',
          text: 'Status blev opdateret for opgave ',
          linkText: taskId,
          linkTo: linkToTask(taskId),
          duration: 10000
        });
        setTaskId('');
        setNewStatus('');
        setReason('');
      })
      .catch((err: ApiException) => {
        try {
          const error = JSON.parse(err.response);
          NotificationService.error(error.detail || 'Der opstod en fejl');
        } catch {
          log(err);
          NotificationService.error('Der opstod en fejl ved opdatering af status');
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <Container>
      <Header>
        <Typography variant="h3">Opdater opgavestatus</Typography>
      </Header>
      <FormContainer>
        <TextField label="Opgave ID" type="number" value={taskId} onChange={(e) => setTaskId(e.target.value)} />
        <Select label="Ny status" value={newStatus} onChange={(e) => setNewStatus(e.target.value as WorkTaskStatus)}>
          {Object.values(WorkTaskStatus)
            .filter((status) => status !== WorkTaskStatus.Created)
            .map((status) => (
              <MenuItem key={status} value={status}>
                {getEnumDisplayValue(status)}
              </MenuItem>
            ))}
        </Select>
        <TextField
          label="Begrundelse"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          helperText="Denne begrundelse vil være synlig for brugere under 'Beskeder' på opgaven"
          multiline
          rows={3}
        />
        <Button
          onClick={handleSubmit}
          disabled={!taskId || newStatus === '' || !reason.trim() || loading}
          isLoading={loading}
        >
          Opdater status
        </Button>
      </FormContainer>
    </Container>
  );
};

const Container = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  text-align: start;
  height: 100%;

  & > *:nth-child(1) {
    padding: ${(props) => props.theme.spacing(7)} ${(props) => props.theme.spacing(7)} 0px
      ${(props) => props.theme.spacing(7)};
  }
`;

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 500px;
  padding: ${(props) => props.theme.spacing(7)};
  margin: ${(props) => props.theme.spacing(7)};
  gap: ${(props) => props.theme.spacing(4)};
  background-color: white;
  border-radius: 12px;
  border: 1px solid ${(props) => props.theme.palette.grey.black10};
`;

export default AdminUpdateTaskStatusView;
